import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { IComboSlotOption, IItem, IItemOption } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { useModifierRedesignPhaseTwo } from 'experiments/use-modifier-redesign-phase-two';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { useProductWizardFlow } from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import {
  ModifierTypes,
  ProductWizardStep,
  UserSelectionModifierMap,
  UserSelectionModifierSlotMap,
} from 'state/product-wizard-v2/types';
import { ProductWizardUtils } from 'state/product-wizard-v2/utils';
import { useScrollResetOnMount } from 'state/scroll/hooks';
import { isCombo, isItem, isPicker } from 'utils/menu';
import { getDefaultComboSlotOption } from 'utils/menu/get-default-combo-slot-option';

import { getModifierType } from '../modifier/utils';
import { UnsavedChangesDialog } from '../product-detail-flow/dialogs/unsaved-changes-dialog';

import { ComboSlotType } from './ComboSlotType';
import { ProductCustomizationItem as ProductCustomizationItemExperiment } from './experiment/product-customization-item';
import { getComboSlotType as getExperimentComboSlotType } from './experiment/utils';
import { ProductCustomizationDrinks } from './product-customization-drinks';
import { ProductCustomizationItem } from './product-customization-item';
import { ProductCustomizationPicker } from './product-customization-picker';
import { ProductCustomizationSides } from './product-customization-sides';
import { ProductCustomizationPanel } from './styled';
import { IProductCustomizationProps, SelectModifierFn } from './types';
import { getComboSlotType, getModifiersDefaultOptions } from './utils';

export const ProductCustomization: React.FC<IProductCustomizationProps> = ({
  currentStep,
  filteredPickerAspects,
  getPreviousPickerSelectionIdentifiers,
  selectedPickerAspects,
  setSelectedPickerAspects,
}) => {
  const {
    submitCustomizations,
    userSelections,
    menuObject,
    updateSelectionStateWithId,
    productWizardFlowSteps,
    resetProductWizard,
    setIsBackNavigation,
    productWizardFlowUserSelections,
    singleOrMultipleModifierSelection,
    updateSingleOrMultipleModifierSelection,
    selectedProduct,
    setSingleOrMultipleModifierSelection,
  } = useProductWizardContext();

  const {
    slotKeyToCustomize,
    goToHome,
    goToReviewProduct,
    goToCustomizeProduct,
    isFromCart,
  } = useProductWizardFlow();

  const enableModifierRedesignExperiment = useModifierRedesignPhaseTwo();

  const shouldDisplayConfirmationDialog = React.useRef<boolean>(false);

  const itemToCustomize: IItem | null =
    userSelections.comboSlot?.get(slotKeyToCustomize)?.selectedItem || null;

  const [selectedItem, setSelectedItem] = useState<IItem | null>(itemToCustomize);

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);

  const isMenuObjectPicker = isPicker(menuObject);
  const isSelectedItemAnItem = isItem(selectedItem);
  const isItemWithModifiers = isSelectedItemAnItem && selectedItem.options.length;

  const [modifierSelections, setModifierSelections] = useState<UserSelectionModifierSlotMap>(
    userSelections.modifiers[slotKeyToCustomize]
  );

  const [defaultModifiersMap] = useState(() =>
    selectedItem ? getModifiersDefaultOptions(selectedItem) : {}
  );

  const comboSlot = useMemo(() => userSelections.comboSlot?.get(slotKeyToCustomize)?.comboSlot, [
    slotKeyToCustomize,
    userSelections.comboSlot,
  ]);

  const comboSlotOptions = useMemo(() => comboSlot?.options || [], [comboSlot?.options]);
  const comboSlotType: ComboSlotType | null = useMemo(() => {
    if (!comboSlot) {
      return null;
    }

    if (enableModifierRedesignExperiment) {
      return getExperimentComboSlotType(comboSlot);
    }

    return getComboSlotType(getDefaultComboSlotOption(comboSlot)?.option as IItem);
  }, [comboSlot, enableModifierRedesignExperiment]);

  useScrollResetOnMount();

  useEffect(() => {
    setSelectedItem(itemToCustomize);
    setModifierSelections(userSelections.modifiers[slotKeyToCustomize]);
  }, [itemToCustomize, slotKeyToCustomize, userSelections.modifiers]);

  const handleNavigateBack = useCallback(() => {
    return currentStep === ProductWizardStep.ProductEditOrder ? goToReviewProduct() : goToHome();
  }, [currentStep, goToReviewProduct, goToHome]);

  const handleSubmit = useCallback(() => {
    setOpenConfirmationDialog(true);
    if (selectedItem) {
      submitCustomizations({
        modifiers: modifierSelections,
        selectedItem,
        slotKey: slotKeyToCustomize,
      });
    }

    return handleNavigateBack();
  }, [
    modifierSelections,
    selectedItem,
    slotKeyToCustomize,
    submitCustomizations,
    handleNavigateBack,
  ]);

  const handleResetModifierSelections = useCallback(
    (item = selectedItem, shouldSubmitCustomizations = true) => {
      const defaultModifiers = item
        ? ProductWizardUtils.ComputeDefaultModifiersSelections(item)
        : {};
      setModifierSelections(defaultModifiers);

      if (shouldSubmitCustomizations && selectedItem) {
        submitCustomizations({
          modifiers: defaultModifiers,
          selectedItem,
          slotKey: slotKeyToCustomize,
        });
      }
    },
    [selectedItem, slotKeyToCustomize, submitCustomizations]
  );

  const handleComboSlotSelectionChange = useCallback(
    (comboSlotOption: IComboSlotOption) => {
      const item = comboSlotOption.option as IItem;
      setSelectedItem(item);
      handleResetModifierSelections(item, false);

      updateSelectionStateWithId({
        id: slotKeyToCustomize,
        isDirty: true,
        type: MenuObjectTypes.COMBO_SLOT,
      });

      shouldDisplayConfirmationDialog.current = true;

      const foundSingleOrMultipleChoiceOption = (
        (comboSlotOption.option.options as IItemOption[]) || []
      ).find(modifier => {
        const modifierType = getModifierType(modifier);
        return [ModifierTypes.MULTIPLE_CHOICE, ModifierTypes.SINGLE_CHOICE].includes(modifierType);
      });

      setSingleOrMultipleModifierSelection(prev => {
        if (foundSingleOrMultipleChoiceOption) {
          return {
            ...(prev ?? {}),
            [slotKeyToCustomize]: {
              isPristine: true,
              isSaved: false,
              type: getModifierType(foundSingleOrMultipleChoiceOption),
              maxAmount: foundSingleOrMultipleChoiceOption.maxAmount,
              name: foundSingleOrMultipleChoiceOption.name.locale,
              key: foundSingleOrMultipleChoiceOption._key,
            },
          };
        }

        // If there are no previous selections, return null.
        if (prev == null) {
          return null;
        }

        // Remove the slotKeyToCustomize from the previous selection to avoid having multiple selections for the same slot.
        const next = { ...prev };
        delete next[slotKeyToCustomize];
        return Object.keys(next).length ? next : null;
      });

      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.PRODUCT_COMBO_SLOT_SELECTION_UPDATE,
          name: item.name.locale ?? '',
          Id: item._id,
        },
      });
    },
    [
      handleResetModifierSelections,
      setSingleOrMultipleModifierSelection,
      slotKeyToCustomize,
      updateSelectionStateWithId,
    ]
  );

  const handleChangeModifierSelections = useCallback<SelectModifierFn>(
    (selections, modifier) => {
      if (selectedItem) {
        const mappedSelections = Object.values(selections).reduce((acc, selection) => {
          acc[selection._key] = {
            item: selectedItem,
            itemId: selectedItem._id,
            modifier: selection,
            option: modifier,
            comboSlotId: userSelections?.comboSlot?.get(slotKeyToCustomize)?.comboSlot?._id,
            isSanityDefaultSelection: false,
          };
          return acc;
        }, {} as UserSelectionModifierMap);

        setModifierSelections(current => ({
          ...current,
          [modifier._key]: mappedSelections,
        }));

        shouldDisplayConfirmationDialog.current = true;
      }
    },
    [selectedItem, slotKeyToCustomize, userSelections]
  );

  const discardUnsavedChanges = useCallback(() => {
    setOpenConfirmationDialog(false);
    return handleNavigateBack();
  }, [handleNavigateBack]);

  const pickerAspectExclusions = useMemo(
    () => ProductWizardUtils.GetPickerAspectExclusions(menuObject),
    [menuObject]
  );

  const handleBackNavigationForEditOrder = React.useCallback(() => {
    if (
      shouldDisplayConfirmationDialog.current &&
      currentStep === ProductWizardStep.ProductEditOrder
    ) {
      return setOpenConfirmationDialog(true);
    }
    goToReviewProduct();
  }, [currentStep, goToReviewProduct]);

  const handleBackNavigationForCombo = React.useCallback(() => {
    if (shouldDisplayConfirmationDialog.current) {
      return setOpenConfirmationDialog(true);
    }
    goToHome();
  }, [goToHome]);

  const foundCurrentFlowStep = productWizardFlowSteps.find(step => step.currentStep);
  const currentIndex = foundCurrentFlowStep
    ? productWizardFlowSteps.indexOf(foundCurrentFlowStep)
    : 1;
  const previousFlowStep = productWizardFlowSteps.at(currentIndex - 1);
  const previousIsFirstFlowStep =
    productWizardFlowSteps.findIndex(item => item.id === previousFlowStep?.id) === 0;

  const handleBackNavigationForPicker = React.useCallback(() => {
    // If we are reviewing a specific combo slot in the picker flow.
    // then we shoud redirect to the product review screen.
    if (currentStep === ProductWizardStep.ProductEditOrder) {
      return goToReviewProduct();
    }

    // Otherwise save made selections and go to the previous step.
    const foundCustomizations = productWizardFlowUserSelections.find(
      selection => selection.id === slotKeyToCustomize && !!selection.isDirty
    );

    if (selectedItem && modifierSelections && !!foundCustomizations) {
      submitCustomizations({
        slotKey: slotKeyToCustomize,
        selectedItem,
        modifiers: modifierSelections,
      });

      if (singleOrMultipleModifierSelection?.[slotKeyToCustomize]) {
        updateSingleOrMultipleModifierSelection(slotKeyToCustomize, { isSaved: true });
      }
    }

    return goToCustomizeProduct({
      slotKey: previousFlowStep?.id ?? '',
      step: previousIsFirstFlowStep
        ? ProductWizardStep.ProductHome
        : ProductWizardStep.ProductCustomization,
    });
  }, [
    currentStep,
    productWizardFlowUserSelections,
    selectedItem,
    modifierSelections,
    goToCustomizeProduct,
    previousFlowStep?.id,
    previousIsFirstFlowStep,
    goToReviewProduct,
    slotKeyToCustomize,
    submitCustomizations,
    singleOrMultipleModifierSelection,
    updateSingleOrMultipleModifierSelection,
  ]);

  const onPressBackNavigation = React.useCallback(() => {
    setIsBackNavigation(true);

    if (currentStep === ProductWizardStep.ProductEditOrder) {
      return handleBackNavigationForEditOrder();
    }

    return isCombo(menuObject) ? handleBackNavigationForCombo() : handleBackNavigationForPicker();
  }, [
    currentStep,
    handleBackNavigationForCombo,
    handleBackNavigationForEditOrder,
    handleBackNavigationForPicker,
    menuObject,
    setIsBackNavigation,
  ]);

  const backNavigationLabel = React.useMemo(() => {
    if (isCombo(menuObject)) {
      return t('productDetailGoBack', { screen: menuObject.name.locale ?? '' });
    } else if (isPicker(menuObject)) {
      if (previousIsFirstFlowStep) {
        return t('productDetailPickerGoBack', { screen: selectedProduct.name.locale ?? '' });
      }

      if (previousFlowStep?.type === 'pickerAspect') {
        const foundPickerAspect = menuObject.pickerAspects.find(pa => {
          return pa._id === previousFlowStep.id;
        });

        const pickerAspectName = foundPickerAspect?.name.locale ?? '';

        return t('productDetailPickerCustomizationGoBack', { screen: pickerAspectName });
      }

      // Otherwise get back navigation label from previous selected item customization screen
      const previousSelectedItem =
        (previousFlowStep && userSelections.comboSlot?.get(previousFlowStep.id)?.selectedItem) ??
        null;

      return t('productDetailPickerCustomizationGoBack', {
        screen: previousSelectedItem?.name.locale ?? '',
      });
    }
    return '';
  }, [
    menuObject,
    previousFlowStep,
    previousIsFirstFlowStep,
    selectedProduct.name.locale,
    userSelections.comboSlot,
  ]);

  const ProductCustomizationItemComponent = enableModifierRedesignExperiment
    ? ProductCustomizationItemExperiment
    : ProductCustomizationItem;

  const renderProductCustomizationBySlotType = useMemo(() => {
    const sharedCustomizationProps = {
      comboSlot,
      comboSlotOptions,
      currentStep,
      defaultModifiersMap,
      handleChangeModifierSelections,
      handleComboSlotSelectionChange,
      handleResetModifierSelections,
      modifierSelections,
      pickerAspectExclusions,
      selectedItem,
      slotKeyToCustomize,
      onPressBackNavigation,
      backNavigationLabel,
    };

    // @TODO: Add docs explaining this scenario
    if (!comboSlot && isItemWithModifiers) {
      return <ProductCustomizationItemComponent {...sharedCustomizationProps} />;
    }

    switch (comboSlotType) {
      case ComboSlotType.SIDES:
        return (
          <ProductCustomizationSides comboSlotType={comboSlotType} {...sharedCustomizationProps} />
        );
      case ComboSlotType.DRINKS:
        return (
          <ProductCustomizationDrinks comboSlotType={comboSlotType} {...sharedCustomizationProps} />
        );
      default:
        return <ProductCustomizationItemComponent {...sharedCustomizationProps} />;
    }
  }, [
    ProductCustomizationItemComponent,
    backNavigationLabel,
    comboSlot,
    comboSlotOptions,
    comboSlotType,
    currentStep,
    defaultModifiersMap,
    handleChangeModifierSelections,
    handleComboSlotSelectionChange,
    handleResetModifierSelections,
    isItemWithModifiers,
    modifierSelections,
    onPressBackNavigation,
    pickerAspectExclusions,
    selectedItem,
    slotKeyToCustomize,
  ]);

  const renderProductCustomizationForPicker = useMemo(() => {
    const userSelectedPickerAspects = userSelections.pickerAspects;

    if (
      isMenuObjectPicker &&
      userSelectedPickerAspects &&
      userSelectedPickerAspects[slotKeyToCustomize]
    ) {
      return (
        <ProductCustomizationPicker
          pickerData={menuObject}
          pickerSlotNumberToCustomize={slotKeyToCustomize}
          userSelectedPickerAspects={userSelectedPickerAspects}
          filteredPickerAspects={filteredPickerAspects}
          getPreviousPickerSelectionIdentifiers={getPreviousPickerSelectionIdentifiers}
          selectedPickerAspects={selectedPickerAspects}
          setSelectedPickerAspects={setSelectedPickerAspects}
          onPressBackNavigation={onPressBackNavigation}
          backNavigationLabel={backNavigationLabel}
        />
      );
    }

    return renderProductCustomizationBySlotType;
  }, [
    backNavigationLabel,
    filteredPickerAspects,
    getPreviousPickerSelectionIdentifiers,
    isMenuObjectPicker,
    menuObject,
    onPressBackNavigation,
    renderProductCustomizationBySlotType,
    selectedPickerAspects,
    setSelectedPickerAspects,
    slotKeyToCustomize,
    userSelections.pickerAspects,
  ]);

  React.useEffect(() => {
    const foundFirstStep = productWizardFlowSteps.find(step => !!step.isFirstStep);

    const shouldRedirectToHome =
      isPicker(menuObject) &&
      !foundFirstStep &&
      currentStep !== ProductWizardStep.ProductHome &&
      !isFromCart;

    if (shouldRedirectToHome) {
      resetProductWizard();
      goToHome();
    }
  }, [currentStep, goToHome, isFromCart, menuObject, productWizardFlowSteps, resetProductWizard]);

  if (!selectedItem && !isMenuObjectPicker) {
    return null;
  }

  return (
    <>
      <ProductCustomizationPanel testID="product-customization">
        {isMenuObjectPicker
          ? renderProductCustomizationForPicker
          : renderProductCustomizationBySlotType}
      </ProductCustomizationPanel>
      {/* Confirmation Dialogs */}
      <UnsavedChangesDialog
        showDialog={openConfirmationDialog}
        onConfirm={handleSubmit}
        onCancel={discardUnsavedChanges}
      />
    </>
  );
};
